/**
 * /downhole/wells — the Directional & MWD well-job register (api#1055,
 * `GET /downhole/jobs`). One job per well. Gated by DdModeGate: the API
 * answers 409 DD_MODE_OFF to every door while the company's mode is off.
 *
 * COUNTS ARE A TALLY OF THE LOADED ROWS, and that is honest here only because
 * `GET /jobs` is unpaginated (no LIMIT in the handler) and no endpoint returns
 * per-status counts. Status is therefore filtered on the client over one read,
 * so every chip counts the same rows the register shows. Search and customer
 * go to the API (`q`, `customer_id`), so with either set the chips count the
 * matching jobs, and the footer says "matching". The customer filter is in the
 * URL (`?customer=`) beside `?status=`, so a filtered register can be linked.
 *
 * THE VOCABULARY IS THE API'S (lib/dh-jobs.ts): scheduled → mobilizing →
 * on_location → released → completed, cancel from the first two. An unknown
 * status is a slate dash with no rail.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CalendarClock, ChevronRight, CircleDot, Loader2, MapPin, Plus, Search, Truck } from 'lucide-react';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { useDownholeJobs } from '../../hooks/useDownholeJobs';
import { formatCalendarDate, formatInstantDay } from '../../lib/calendar-date';
import { fmtQty } from '../../lib/display-labels';
import { DH_JOB_STATUS_SKIN, dhSkin } from '../../lib/downhole-labels';
import { canWriteDownholeJobs } from '../../lib/downhole-roles';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { DH_JOB_STATUSES, type DhJob, type DhJobDetail } from '../../types/downhole';
import { DdModeGate } from './DdModeGate';
import { DhBadge, DhEmpty, DhPanel } from './DownholeBits';
import { JobFormDialog, useCustomers } from './WellJobDialogs';

const isStatus = (v: string | null): v is (typeof DH_JOB_STATUSES)[number] =>
  !!v && (DH_JOB_STATUSES as readonly string[]).includes(v);

/** UWI first, API number second: whichever names the well. */
export function wellIdentifier(job: Pick<DhJob, 'uwi' | 'api_number'>): string | null {
  return job.uwi || job.api_number || null;
}

export default function WellsScreen() {
  return (
    <DdModeGate>
      <WellsRegister />
    </DdModeGate>
  );
}

function WellsRegister() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = canWriteDownholeJobs(user?.role);
  const [searchParams, setSearchParams] = useSearchParams();
  const statusParam = searchParams.get('status');
  const status = isStatus(statusParam) ? statusParam : '';
  const customer = searchParams.get('customer') ?? '';
  const customersQuery = useCustomers(true);
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);

  // The API searches job number, well, UWI, API number, LSD, rig and customer.
  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const params = useMemo(
    () => (q || customer ? { ...(q && { q }), ...(customer && { customer_id: customer }) } : undefined),
    [q, customer],
  );
  const listQuery = useDownholeJobs(params, true);
  const all = useMemo(() => listQuery.data?.data ?? [], [listQuery.data]);
  const jobs = status ? all.filter((j) => j.status === status) : all;
  const tz = listQuery.data?.meta?.time_zone ?? null;
  const failure = queryLoadFailure(listQuery);
  // A count is shown only for a delivered list; loading or failed shows none.
  const loaded = !listQuery.isLoading && !failure && !!listQuery.data;
  const count = (s?: string) => (loaded ? (s ? all.filter((j) => j.status === s).length : all.length) : undefined);
  const runsOpen = loaded ? all.filter((j) => j.run_open === true).length : undefined;

  const setParam = (key: 'status' | 'customer', next: string) => {
    const nextParams = new URLSearchParams(searchParams);
    if (next) nextParams.set(key, next);
    else nextParams.delete(key);
    setSearchParams(nextParams, { replace: true });
  };
  const setStatus = (next: string) => setParam('status', next);
  const narrowed = !!(status || q || customer);
  const serverNarrowed = !!(q || customer);

  const open = (id: string) => navigate(`/downhole/wells/${id}`);
  const rowKeys = (id: string) => (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      open(id);
    }
  };
  const day = (v: string | null) => formatInstantDay(v, tz) ?? '—';

  const chips = [{ key: '', label: 'All', count: count() }, ...DH_JOB_STATUSES.map((s) => ({ key: s, label: dhSkin(DH_JOB_STATUS_SKIN, s).label, count: count(s) }))];

  return (
    <div data-testid="downhole-wells-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Directional & MWD"
        title="Wells"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          canWrite ? (
            <button type="button" onClick={() => setCreating(true)} className="fi-action fi-action--standard fi-action--primary">
              <Plus className="size-4" aria-hidden="true" />
              New well job
            </button>
          ) : undefined
        }
        stats={[
          { icon: <CalendarClock className="size-4" aria-hidden="true" />, label: 'Scheduled', sub: 'Not yet moving', tone: 'accent', loading: listQuery.isLoading, value: displayValue(count('scheduled')) },
          { icon: <MapPin className="size-4" aria-hidden="true" />, label: 'On location', sub: 'At the well', tone: 'info', loading: listQuery.isLoading, value: displayValue(count('on_location')) },
          { icon: <Truck className="size-4" aria-hidden="true" />, label: 'Released', sub: 'To demob', tone: 'success', loading: listQuery.isLoading, value: displayValue(count('released')) },
          { icon: <CircleDot className="size-4" aria-hidden="true" />, label: 'Runs open', sub: 'Tool in hole', tone: 'neutral', loading: listQuery.isLoading, value: displayValue(runsOpen) },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <div className="scrollbar-hide flex gap-2 overflow-x-auto" role="group" aria-label="Filter by status">
            {chips.map((chip) => {
              const active = status === chip.key;
              return (
                <button
                  key={chip.key || 'all'}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setStatus(chip.key)}
                  className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition ${
                    active ? 'bg-fi-amber text-fi-amber-ink' : 'border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text'
                  }`}
                >
                  {chip.label}
                  {chip.count !== undefined && (
                    <span className={`tabular-nums ${active ? 'text-fi-amber-ink' : 'text-fieldiq-muted'}`}>{displayValue(chip.count)}</span>
                  )}
                </button>
              );
            })}
          </div>

          <div className="flex flex-col gap-2 sm:flex-row">
          <label className="relative block flex-1">
            <span className="sr-only">Search job, well, UWI, API number, LSD, rig or customer</span>
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fieldiq-muted" aria-hidden="true" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search job, well, UWI, API number, LSD, rig or customer"
              className="h-10 w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-card pl-9 pr-3 text-sm text-white placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
            />
          </label>
          <select
            aria-label="Customer"
            value={customer}
            onChange={(e) => setParam('customer', e.target.value)}
            className="h-10 rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-3 text-sm text-white focus:border-fi-amber focus:outline-none sm:w-64"
          >
            <option value="">All customers</option>
            {/* A customer in the URL stays selectable even if the list has not arrived. */}
            {customer && !(customersQuery.data ?? []).some((c) => c.id === customer) && <option value={customer}>Selected customer</option>}
            {(customersQuery.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          </div>

          <DhPanel
            eyebrow="Well register"
            title="Well jobs"
            testId="downhole-wells-register"
            footer={loaded && jobs.length > 0 && narrowed ? `Showing ${jobs.length} of ${all.length} ${serverNarrowed ? 'matching ' : ''}well jobs` : undefined}
          >
            {listQuery.isLoading ? (
              <div className="flex justify-center py-16"><Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading well jobs" /></div>
            ) : failure ? (
              <LoadFailureNotice error={queryFailureError(listQuery)} kind={failure} subject="well jobs" />
            ) : jobs.length === 0 ? (
              <DhEmpty>{narrowed ? 'No well job matches these filters.' : 'There are no well jobs yet.'}</DhEmpty>
            ) : (
              <>
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full min-w-[1280px] text-left">
                    <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                      <tr className="border-l-4 border-l-transparent">
                        <th scope="col" className="px-4 py-3">Job</th>
                        <th scope="col" className="px-4 py-3">Well</th>
                        <th scope="col" className="px-4 py-3">Customer</th>
                        <th scope="col" className="px-4 py-3">Rig</th>
                        <th scope="col" className="px-4 py-3">Status</th>
                        <th scope="col" className="px-4 py-3">Mobilized</th>
                        <th scope="col" className="px-4 py-3">On location</th>
                        <th scope="col" className="px-4 py-3 text-right">Assemblies</th>
                        <th scope="col" className="px-4 py-3 text-right">Runs</th>
                        <th scope="col" className="w-[44px] px-4 py-3" />
                      </tr>
                    </thead>
                    <tbody>
                      {jobs.map((j) => (
                        <tr
                          key={j.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => open(j.id)}
                          onKeyDown={rowKeys(j.id)}
                          className={`cursor-pointer border-t border-l-4 border-fieldiq-border outline-none transition hover:bg-fieldiq-dark/50 focus-visible:bg-fi-amber/10 ${dhSkin(DH_JOB_STATUS_SKIN, j.status).rail}`}
                        >
                          <td className="whitespace-nowrap px-4 py-3 font-mono text-sm font-extrabold text-fieldiq-accent-signal">{j.job_number}</td>
                          <td className="min-w-[14rem] px-4 py-3">
                            <p className="text-sm font-bold text-white">{displayValue(j.well_name)}</p>
                            <p className="mt-0.5 font-mono text-xs text-fieldiq-muted">{displayValue(wellIdentifier(j))}</p>
                          </td>
                          <td className="min-w-[12rem] px-4 py-3 text-sm text-fieldiq-text">{displayValue(j.customer_name)}</td>
                          <td className="min-w-[8rem] px-4 py-3 text-sm text-fieldiq-text">{displayValue(j.rig_name)}</td>
                          <td className="px-4 py-3"><DhBadge map={DH_JOB_STATUS_SKIN} value={j.status} /></td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-fieldiq-text">{day(j.mobilized_at)}</td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-fieldiq-text">{day(j.on_location_at)}</td>
                          <td className="px-4 py-3 text-right font-mono text-sm tabular-nums text-fieldiq-text">{fmtQty(j.assembly_count)}</td>
                          <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm tabular-nums text-fieldiq-text">
                            {fmtQty(j.run_count)}
                            {j.run_open === true && <span className="ml-2 rounded-fi-pill bg-fi-signal-blue px-2 py-0.5 text-[0.62rem] font-black uppercase text-fi-signal-ink">In hole</span>}
                          </td>
                          <td className="px-4 py-3"><ChevronRight className="size-4 text-fieldiq-muted" aria-hidden="true" /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="space-y-3 p-4 md:hidden">
                  {jobs.map((j) => (
                    <div
                      key={j.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => open(j.id)}
                      onKeyDown={rowKeys(j.id)}
                      className="cursor-pointer rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-3 outline-none transition hover:border-fieldiq-muted focus-visible:border-fi-amber focus-visible:bg-fi-amber/10"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-mono text-sm font-extrabold text-fieldiq-accent-signal">{j.job_number}</p>
                          <p className="mt-0.5 truncate text-sm font-bold text-white">{displayValue(j.well_name)}</p>
                        </div>
                        <DhBadge map={DH_JOB_STATUS_SKIN} value={j.status} />
                      </div>
                      <p className="mt-2 text-xs text-fieldiq-text">{displayValue(j.customer_name)} · {displayValue(j.rig_name)}</p>
                      <p className="mt-1 text-xs text-fieldiq-muted">
                        Scheduled {formatCalendarDate(j.scheduled_start) ?? '—'} · {fmtQty(j.assembly_count)} assemblies · {fmtQty(j.run_count)} runs
                      </p>
                    </div>
                  ))}
                </div>
              </>
            )}
          </DhPanel>
        </div>
      </section>

      {canWrite && (
        <JobFormDialog
          open={creating}
          job={null}
          onClose={() => setCreating(false)}
          onSaved={(job: DhJobDetail) => navigate(`/downhole/wells/${job.id}`)}
        />
      )}
    </div>
  );
}
